import options from '../../js/config_chart';
import ajax from '@ep/tools/ajax.js';
import storage from '@ep/tools/storage';
import session from '@ep/tools/session';
import toast from '@ep/tools/toast';
import accountsClient from '@ep/auth-client/client';
import { resolveKnownImageSource } from '@ep/components/create/image';
const brandLogo = () => {
  const configured = String(config?.logoUrl || '').trim();
  if (configured) {
    return resolveKnownImageSource(configured, 'product');
  }

  const envLogo = (typeof process !== 'undefined' && process?.env?.BRAND_LOGO_URL)
    ? String(process.env.BRAND_LOGO_URL).trim()
    : '';
  if (envLogo) {
    return resolveKnownImageSource(envLogo, 'product');
  }

  return '';
};

const run = 'chart';
const date = new Date();
const year = date.getFullYear();

function normalizeSlug(rawSlug) {
  if (typeof rawSlug !== 'string') return null;

  const cleaned = rawSlug
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '');

  if (!cleaned || cleaned === 'null' || cleaned === 'undefined') {
    return null;
  }

  return cleaned;
}

function syncLoggedSlug(slug, { replace = false } = {}) {
  const normalized = normalizeSlug(slug);
  if (!normalized) return;

  const path = `/${normalized}`;
  if (window.location.pathname === path) return;

  const method = replace ? 'replaceState' : 'pushState';
  window.history[method]({}, '', path);
}

function hideLoggedSlugInAddressBar() {
  if (window.location.pathname === '/') return;
  window.history.replaceState({}, '', '/');
}

function updateLoggedSeo(sectionLabel) {
  const title = `Admin Digital | ${sectionLabel || 'Area Logada'}`;
  document.title = title;
}

function trackLoggedPageView() {
  const payload = {
    page_title: document.title,
    page_path: window.location.pathname || '/',
    page_location: window.location.href
  };

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', payload);
  }

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: 'page_view', ...payload });
  }
}

/**
 * Encerra a sessão atual e retorna para a home pública.
 * @private
 * @returns {Promise<void>}
 */
async function logout() {
  try {
    await accountsClient.logoutCurrent({ loader: false });
  } catch (error) {
    console.error('Erro ao executar logout:', error);
  } finally {
    session.clear();
    storage.clear();
    window.location.replace('/');
  }
}

function setupUserMenu() {
  const userMenu = document.querySelector('.user-logged');
  const trigger = document.querySelector('.user-menu-trigger');
  if (!userMenu || !trigger) return;

  const closeMenu = () => {
    userMenu.classList.remove('open');
    trigger.setAttribute('aria-expanded', 'false');
  };

  if (window.__userMenuPointerHandler) {
    window.removeEventListener('pointerdown', window.__userMenuPointerHandler, true);
  }

  if (window.__userMenuKeyHandler) {
    document.removeEventListener('keydown', window.__userMenuKeyHandler, true);
  }

  window.__userMenuPointerHandler = (event) => {
    const activeMenu = document.querySelector('.user-logged');
    if (activeMenu && !activeMenu.contains(event.target)) {
      activeMenu.classList.remove('open');
      document.querySelector('.user-menu-trigger')?.setAttribute('aria-expanded', 'false');
    }
  };

  window.__userMenuKeyHandler = (event) => {
    if (event.key === 'Escape') {
      const activeMenu = document.querySelector('.user-logged');
      if (activeMenu) {
        activeMenu.classList.remove('open');
        document.querySelector('.user-menu-trigger')?.setAttribute('aria-expanded', 'false');
      }
    }
  };

  window.addEventListener('pointerdown', window.__userMenuPointerHandler, true);
  document.addEventListener('keydown', window.__userMenuKeyHandler, true);

  closeMenu();
}

/**
 * Monta header e footer do fluxo logado no mesmo padrão visual do unlogged.
 * @private
 * @returns {void}
 */
async function renderShell(routeSlug = null) {
  const dictBranding = (dic?.branding && typeof dic.branding === 'object') ? dic.branding : {};
  const BRAND = {
    logoUrl: dictBranding.logoUrl || brandLogo(),
    title1: dictBranding.title1 || 'NUCLEO',
    title2: dictBranding.title2 || 'P',
    subtitle: dictBranding.subtitle || 'Solucoes Digitais',
    footer: dictBranding.footer || '© {year} • Nucleo P • Menos discurso, mais resultado.',
  };
  const footerHtml = String(BRAND.footer).replaceAll('{year}', String(year));

  document.querySelector('header').empty();
  document.querySelector('footer').empty();
  document.querySelector('aside').empty();
  const notificationsResp = await ajax.post({
    product: 'platform',
    category: 'communication',
    domain: 'communication',
    module: 'notify',
    method: 'getNotificationsCount',
    body: {},
    config: { loader: false },
  });
  const notifications = Number(notificationsResp?.count || 0);
  const menu = await accountsClient.getMenu({ loader: false });
  let initialRouteMatched = !routeSlug;

  await create([
    {
      element: 'a', parent: 'header', href: '/', isGrid: true, content: [
        { element: 'image', origin: 'external', src: BRAND.logoUrl, alt: 'Logo', hasFigure: false, class: 'header-logo' },
        {
          element: 'div',
          class: 'brand-lockup',
          isGrid: true,
          content: [
            {
              element: 'div',
              class: 'brand-title',
              isGrid: true,
              content: [
                { element: 'span', text: BRAND.title1 },
                { element: 'span', class: 'yellow-p', text: BRAND.title2 },
              ]
            },
            { element: 'div', class: 'brand-subtitle', text: BRAND.subtitle }
          ]
        }
      ]
    },
    { element: 'div', parent: 'header' },
    {
      element: 'a', parent: 'header', class: 'm-not m-bell', icon: { type: 'solid', name: 'bell' }, isGrid: true, content: [
        { element: 'span', parent: 'header', class: 'm-not-qty', text: notifications > 99 ? '99+' : notifications.toString() },
      ]
    },
    {
      element: 'div', parent: 'header', class:'user-logged', isGrid: true, content: [
        {
          element: 'a',
          class: 'user-menu-trigger',
          href: '#',
          aria: { haspopup: 'true', expanded: 'false' },
          click: (event) => {
            event.preventDefault();
            event.stopPropagation();
            const wrap = event.currentTarget.closest('.user-logged');
            const isOpen = wrap?.classList.contains('open');
            wrap?.classList.toggle('open', !isOpen);
            event.currentTarget.setAttribute('aria-expanded', (!isOpen).toString());
          },
          isGrid: true,
          content: [
            { element: 'span', text: dic.labels.hello.replace('{name}', session.get('user')) },
            { element: 'image', parent: 'header', origin: 'external', src: session.get('userPhotoURL'), alt: 'avatar', hasFigure: false, class: 'header-avatar' },
            { element: 'span', class: 'user-menu-arrow', aria: { hidden: 'true' }, text: '▼' }
          ]
        },
        {
          element: 'list',
          class: 'user-menu-dropdown',
          isGrid: true,
          content: [
            { element: 'item', isGrid: true, content: [{ element: 'a', href: '#', text: dic.labels.myProfile, click: (event) => event.preventDefault() }] },
            { element: 'item', isGrid: true, content: [{ element: 'a', href: '#', text: dic.labels.preferences, click: (event) => event.preventDefault() }] },
            { element: 'item', isGrid: true, content: [{ element: 'a', href: '#', text: dic.labels.changePassword, click: (event) => event.preventDefault() }] },
            { element: 'item', class: 'separator' },
            {
              element: 'item',
              isGrid: true,
              content: [{
                element: 'a',
                href: '#',
                text: dic.labels.logout,
                click: async (event) => {
                  event.preventDefault();
                  await logout();
                }
              }]
            }
          ]
        }
      ]
    },
    {
      element: 'div',
      parent: 'header',
      class: 'user-menu-backdrop',
      click: () => {
        const wrap = document.querySelector('.user-logged');
        const menuTrigger = document.querySelector('.user-menu-trigger');
        wrap?.classList.remove('open');
        menuTrigger?.setAttribute('aria-expanded', 'false');
      }
    },
    { element: 'div', parent: 'footer', html: footerHtml },
    { element: 'div', parent: 'footer' },
    {
      element: 'menu',
      parent: 'aside',
      items: menu,
      type: 'aside',
      dics: ['suites', 'modules|display', 'methods'],
      route: {
        slug: routeSlug,
        onNavigate: ({ slug, label, source }) => {
          if (source === 'route') {
            initialRouteMatched = true;
            syncLoggedSlug(slug, { replace: true });
          } else {
            hideLoggedSlugInAddressBar();
          }

          updateLoggedSeo(label || slug || 'Area Logada');
          trackLoggedPageView();
        },
        onRouteMiss: ({ slug }) => {
          initialRouteMatched = false;
          toast({
            type: 'warning',
            message: dic?.message?.slugNotFound || `Modulo nao encontrado para o slug "${slug}".`
          });
          window.history.replaceState({}, '', '/');
        }
      }
    }
  ]);

  setupUserMenu();
  return initialRouteMatched;
}

export default async function initLoggedPage() {
  const routeSlug = normalizeSlug(session.get('routeSlug'));
  session.delete('routeSlug');

  const handledBySlug = await renderShell(routeSlug);
  const main = document.querySelector('main');

  if (routeSlug && handledBySlug) {
    return;
  }

  main.empty();

  if (run === 'chart') {
    const options2 = { ...options, type: 'column' };
    const options3 = { ...options, type: 'spline', aggregate: 'avg', unit: 'days' };

    create(
      { element: 'grid', class: 'externo', style: { width: 'calc(100% - 10px)' }, cols: 2, rows: 1, parent: 'main', isGrid: true, content: [options2, options3] },
    );
  } else if (run === 'miniDisplay') {
    const mOptions = {
      element: 'miniDisplay',
      scale: {
        green: 100,
        yellowgreen: 75,
        yellow: 50,
        orange: 25,
        red: 5,
        black: 0
      },
      /* Colocar se quiser o resultado no lugar do número
      scaleName: {
        'Ágil': 100,
        'Avançado': false,
        'Médio': 50,
        'Principiante': false,
        'Tosco': 0,
        black: false
      },
      */
      value: 99,
      limit: null,
      unit: 'respostas',
      label: 'Happiness Door',
      aggregate: 'avg',
    };

    create(
      { element: 'grid', class: 'externo', style: { width: 'fit-content' }, cols: 5, rows: 1, parent: 'main', isGrid: true, content: [mOptions, mOptions, mOptions, mOptions, mOptions] },
    );
  } else if (run === 'grid') {
    create({
      element: 'datagrid',
      parent: 'main',
      server: 'accounts',
      module: 'user_test',
      key: 'main'
    });
  } else if (run === 'form') {
    await create({
      element: 'autoform',
      parent: main,
      type: 'fieldset',
      edit: true,
      server: 'accounts',
      module: 'user_test',
      isDialog: false,
      callback: (res) => { console.log(res); },
      filter: {
        // conditions: [{ key: 'ut.id', operator: '=', value: '256465f6-d6e3-4da9-87f4-347570611d81', raw: false }],
        // key:'main'
      }
    });
  }
}
